import { useIntl } from 'react-intl';

import UFOSegment from '@atlaskit/react-ufo/segment';
import { Editions, ProductKeys } from '@atlassiansox/asc-stargate';

import { useCrossFlowFrontendTouchpoint } from '@/components/cross-flow/touchpoint/use-touchpoint/index';
import { useTheme } from '@/libraries/design/utils/dark-theme-provider';
import { useLocalizedHref } from '@/libraries/web-framework/src/localization/use-localized-href';
import { LogoV3 } from '@/libraries/wpl/foundations/src/logo-v3';
import { MobileNavLinkContentProps } from '@/patterns/global-nav/src/mobile-nav/mobile-nav-links-container';
import { ProductPageNav } from '@/patterns/global-nav/src/product-page-nav';

import { WhyBitbucketDropdown } from './why-bitbucket-dropdown';

const BitbucketLogoComponent = () => {
	const { isDarkTheme } = useTheme();
	return <LogoV3 brand="bitbucket" type="logo" appearance="brand" darkTheme={isDarkTheme} />;
};

export const BitbucketNav = () => {
	const intl = useIntl();

	const signupLink = useLocalizedHref('/try/cloud/signup?bundle=bitbucket&edition=free');
	const { openCrossFlow } = useCrossFlowFrontendTouchpoint({
		productKey: ProductKeys.BITBUCKET,
		edition: Editions.FREE,
		redirectUrl: signupLink,
	});
	const dataCenterHref = useLocalizedHref('/software/bitbucket/enterprise/data-center');
	const pricingHref = useLocalizedHref('/software/bitbucket/pricing');
	const links = [
		{
			globalLinksKey: 'whyBitbucket',
			linkText: intl.formatMessage({
				id: 'BitbucketPageTemplate.navbar.whyBitbucket',
				defaultMessage: 'Why Bitbucket',
			}),
			productKey: 'bitbucket',
			hasDropdown: true,
			dropdownType: 'why-bitbucket',
		},
		{
			globalLinksKey: 'self-hosted',
			linkText: intl.formatMessage({
				id: 'BitbucketPageTemplate.navbar.selfhosted',
				defaultMessage: 'Self-Hosted',
			}),
			hasDropdown: false,
			productKey: 'bitbucket',
			href: dataCenterHref,
		},
		{
			globalLinksKey: 'pricing',
			linkText: intl.formatMessage({
				id: 'BitbucketPageTemplate.navbar.pricing',
				defaultMessage: 'Pricing',
			}),
			hasDropdown: false,
			productKey: 'bitbucket',
			href: pricingHref,
		},
	];

	return (
		<UFOSegment name="confluence-pt-cfind-1234-BitbucketNav">
			<ProductPageNav
				productKey={'bitbucket'}
				productLogoUrl="/software/bitbucket"
				productLogo={<BitbucketLogoComponent />}
				productLogoAriaLabel={intl.formatMessage({
					id: 'BitbucketPageTemplate.navbar.bitbucket-logo',
					defaultMessage: 'Bitbucket logo',
				})}
				mobileCtaText={intl.formatMessage({
					id: 'BitbucketPageTemplate.navbar.getitfree',
					defaultMessage: 'Get it Free',
				})}
				navigationTrynowCtaText={intl.formatMessage({
					id: 'BitbucketPageTemplate.navbar.getitfree',
					defaultMessage: 'Get it Free',
				})}
				mobileCtaUrl={signupLink}
				mobileCtaOnClick={openCrossFlow}
				mobileLogo={<BitbucketLogoComponent />}
				productPageNavLinks={[
					{
						globalLinksKey: 'whyBitbucket',
						linkText: intl.formatMessage({
							id: 'BitbucketPageTemplate.navbar.whyBitbucket',
							defaultMessage: 'Why Bitbucket',
						}),
						containerStyles: {
							width: 'fit-content',
							margin: 0,
						},
						hasDropdown: true,
						content: <WhyBitbucketDropdown productKey={'bitbucket'} />,
					},
					{
						globalLinksKey: 'self-hosted',
						linkText: intl.formatMessage({
							id: 'BitbucketPageTemplate.navbar.selfhosted',
							defaultMessage: 'Self-Hosted',
						}),
						hasDropdown: false,
						productKey: 'bitbucket',
						href: dataCenterHref,
					},
					{
						globalLinksKey: 'pricing',
						linkText: intl.formatMessage({
							id: 'BitbucketPageTemplate.navbar.pricing',
							defaultMessage: 'Pricing',
						}),
						hasDropdown: false,
						productKey: 'bitbucket',
						href: pricingHref,
					},
				]}
				navigationTrynowCtaUrl={signupLink}
				navigationTrynowCtaOnClick={openCrossFlow}
				mobileLinksSections={[
					{
						mobileLinkKey: 'mobile-bitbucket',
						mobileLinksProps: links as MobileNavLinkContentProps[],
					},
				]}
			/>
		</UFOSegment>
	);
};
